'use client';

import { useTheme } from '@mui/material';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ImageGalleryFragmentFragmentDoc } from '@packages/gql/generated/shopping/ImageGalleryFragmentFragmentDoc';
import { useResponsiveAkamaiTemplate } from '@packages/modules/src/hooks/useResponsiveAkamaiTemplate/useResponsiveAkamaiTemplate';
import { Image } from '@packages/shared/src/ui/Image/Image';
import type { CarouselThumbnailConfig } from '@packages/shared/src/components/Carousel/types';
import { DynamicYield } from '@packages/shared/src/components/DynamicYield/DynamicYield';
import { Carousel } from '@packages/shared/src/components/Carousel/Carousel/Carousel';
import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { Enlarge32 } from '@packages/themes/icons';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { akamaiLoader } from '@packages/shared/src/ui/Image/loader';
import type { ReactNode } from 'react';
import { forwardRef, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Cliplister } from '../Cliplister';
import { ImageGalleryCallToActionButton } from '../ImageGalleryCallToActionButton';
import { ZoomDialog } from '../ZoomDialog';
import { ErrorFallback } from '../ZoomDialog/ErrorFallback';
import { ImageGalleryActionsWrapper } from './ImageGalleryActionsWrapper';
import { ImageGalleryFlags } from './ImageGalleryFlags';
import { ImageGalleryThumbnail } from './ImageGalleryThumbnail';
import { imageGalleryMessages } from './messages';

// TODO(api-layer): add alt field to media type
/* GraphQL */ `
  fragment ImageGalleryFragment on Product {
    name
    orderNumber
    additionalFlags {
      type
      text
      src
    }
    cliplister(lang: $lang) @skip(if: $skipCliplister) {
      url
    }
    media {
      type
      url
    }
    ...CliplisterFragment
    ...LightboxFragment
  }
`;

type ImageGalleryProps = {
  data: FragmentType<typeof ImageGalleryFragmentFragmentDoc>;
  colorPicker?: ReactNode;
};

const thumbnailConfig: CarouselThumbnailConfig = {
  visibleSlides: 'auto',
  gap: 1.5,
  thumbnails: [],
  thumbnailBoxStyles: (isActiveSlide) => ({
    opacity: 1,
    border: `2px solid transparent`,
    borderColor: isActiveSlide ? 'text.dark' : 'transparent',
    borderRadius: '4px',
    position: 'relative',
    ...(isActiveSlide && {
      '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        border: '1px solid white',
      },
    }),
  }),
};

/**
 * ImageGallery on detailview
 * */
export const ImageGallery = forwardRef<HTMLDivElement, ImageGalleryProps>(
  ({ data, colorPicker }, ref) => {
    const product = getFragmentData(ImageGalleryFragmentFragmentDoc, data);

    const { media, additionalFlags, name } = product;
    const { isMobile } = useDeviceType();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [zoomDialogOpen, setZoomDialogOpen] = useState(false);
    const parentRef = useRef<HTMLDivElement>(null);
    const { templateName: galleryTemplateName } = useResponsiveAkamaiTemplate('gallery');
    const { dynamicYieldEnabled } = useConfig();
    const theme = useTheme();
    const { formatMessage } = useIntl();

    thumbnailConfig.thumbnails = media.map(({ url }) => (
      <ImageGalleryThumbnail imgSrc={url} alt={name} key={url} />
    ));

    const handleImageClick = () => {
      setZoomDialogOpen(true);
    };
    const handleZoomDialogClose = () => {
      setZoomDialogOpen(false);
    };

    return (
      <>
        <div ref={parentRef} className="h-full" id="image-gallery">
          {/* transform: 'scale(1)' overrides the viewport for display: 'fixed' property of underlying ArrowButton component */}
          <div className="sticky top-0 w-full scale-100" ref={ref}>
            <Carousel
              index={selectedIndex}
              showArrows={!isMobile}
              thumbnailConfig={thumbnailConfig}
              aria={{
                buttonPrev: formatMessage(imageGalleryMessages.ariaPrevImage),
                buttonNext: formatMessage(imageGalleryMessages.ariaNextImage),
              }}
              onChange={(index) => setSelectedIndex(index)}
            >
              {media.map(({ url }, i) => (
                <div className="relative h-0 w-full cursor-pointer pt-[100%]" key={url}>
                  {/* TODO(api-layer): add alt field to media type */}
                  <Image
                    loading={i !== 0 ? 'lazy' : 'eager'}
                    priority={i === 0}
                    src={`${url}?${galleryTemplateName}`}
                    alt={name}
                    style={{ objectFit: 'contain' }}
                    fill
                    fetchPriority={i === 0 ? 'high' : 'low'}
                    onClick={handleImageClick}
                    data-testid={i}
                    // unset loader for preloaded image to cache it into google cdn
                    loader={i === 0 ? undefined : akamaiLoader}
                  />
                </div>
              ))}
            </Carousel>
            <ImageGalleryActionsWrapper isMobile={isMobile}>
              <Cliplister data={product} />
              <ImageGalleryCallToActionButton
                onClick={handleImageClick}
                buttonAriaLabel={formatMessage(imageGalleryMessages.zoom)}
              >
                <Enlarge32 sx={{ fill: theme.palette.text.dark }} />
              </ImageGalleryCallToActionButton>
              {isMobile && colorPicker}
            </ImageGalleryActionsWrapper>
            {dynamicYieldEnabled && (
              <>
                <DynamicYield dataDyId="dv_social-proof-inject" />
                <DynamicYield dataDyId="dv_social-proof" />
              </>
            )}
            <ImageGalleryFlags flags={additionalFlags} />
          </div>
        </div>
        {zoomDialogOpen && (
          <ErrorBoundary
            fallback={(reset) => (
              <ErrorFallback
                onClose={() => {
                  handleZoomDialogClose();
                  reset();
                }}
              />
            )}
          >
            <ZoomDialog
              data={product}
              carouselIndex={selectedIndex}
              open={zoomDialogOpen}
              close={handleZoomDialogClose}
            />
          </ErrorBoundary>
        )}
      </>
    );
  },
);
